import type {
  NotificationCategory,
  NotificationInboxItemDto,
  NotificationPriority
} from "@madamuh/domain";
import { Link } from "react-router-dom";
import { formatTenantDateTime } from "../lib/tenantDateTime";
import { describeStatus } from "../lib/uiLabels";

const categoryLabels: Record<NotificationCategory, string> = {
  guardianLinkCompleted: "保護者連携完了",
  guardianLinkRequestRejected: "連携リクエスト未承認",
  schedulePublished: "予定公開",
  occurrenceChanged: "予定変更",
  occurrenceCancelled: "休講",
  replacementVideoPublished: "代替動画公開",
  videoPublished: "動画公開",
  eventReminder: "イベント案内",
  teachingAssignmentChanged: "担当変更",
  todayOccurrenceChanged: "担当予定変更",
  absenceRequestApproved: "欠勤申請承認",
  absenceRequestRejected: "欠勤申請差戻し",
  absenceRequestSubmitted: "欠勤申請提出",
  scheduleValidationWarning: "スケジュール警告",
  schedulePublishRequired: "公開待ち",
  guardianLinkRequestSubmitted: "保護者連携申請",
  studentAbsenceReported: "欠席連絡",
  studentAbsenceCancelled: "欠席取消",
  memberCommunicationPublished: "連絡事項公開",
  memberCommunicationUpdated: "連絡事項更新",
  videoUploadFailed: "動画アップロード失敗"
};

const priorityLabels: Record<NotificationPriority, string> = {
  critical: "緊急",
  high: "高",
  normal: "通常",
  low: "低"
};

function priorityBackground(priority: NotificationPriority): string {
  if (priority === "critical") {
    return "#ffe4e6";
  }

  if (priority === "high") {
    return "#fff1f1";
  }

  if (priority === "low") {
    return "#f4f4f5";
  }

  return "#eef6ff";
}

function readStateLabel(item: NotificationInboxItemDto): string {
  return item.readAt ? "既読" : "未読";
}

function renderRelatedStudents(
  relatedStudentIds: readonly string[],
  studentNameById: Record<string, string>
): string[] {
  return relatedStudentIds.map((studentId) => studentNameById[studentId] ?? studentId);
}

export function NotificationCard({
  item,
  studentNameById = {},
  onMarkRead,
  markReadBusy = false,
  markReadEnabled = false,
  timeZone = "Asia/Tokyo",
  tenantSlug
}: {
  item: NotificationInboxItemDto;
  studentNameById?: Record<string, string>;
  onMarkRead?: (item: NotificationInboxItemDto) => void;
  markReadBusy?: boolean;
  markReadEnabled?: boolean;
  timeZone?: string;
  tenantSlug?: string;
}) {
  const relatedStudentLabels = renderRelatedStudents(item.relatedStudentIds, studentNameById);
  const communicationLink =
    tenantSlug && item.targetType === "memberCommunication" && item.targetId
      ? `/t/${tenantSlug}/member-communications/${item.targetId}`
      : null;

  return (
    <article
      style={{
        border: "1px solid #e5e5e5",
        borderRadius: 12,
        padding: 12,
        display: "grid",
        gap: 10
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div style={{ display: "grid", gap: 4 }}>
          {communicationLink ? (
            <Link to={communicationLink} style={{ color: "inherit", textDecoration: "none" }}>
              <strong>{item.title}</strong>
            </Link>
          ) : (
            <strong>{item.title}</strong>
          )}
          <span style={{ fontSize: 13, opacity: 0.8 }}>{formatTenantDateTime(item.createdAt, timeZone)}</span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-end" }}>
          <span
            style={{
              fontSize: 12,
              padding: "4px 8px",
              borderRadius: 999,
              background: "#f6f6f6"
            }}
          >
            {categoryLabels[item.category]}
          </span>
          <span
            style={{
              fontSize: 12,
              padding: "4px 8px",
              borderRadius: 999,
              background: priorityBackground(item.priority)
            }}
          >
            {priorityLabels[item.priority]}
          </span>
          <span
            style={{
              fontSize: 12,
              padding: "4px 8px",
              borderRadius: 999,
              background: item.readAt ? "#eef6ff" : "#fff8db"
            }}
          >
            {readStateLabel(item)}
          </span>
        </div>
      </div>

      <div style={{ fontSize: 14, lineHeight: 1.5 }}>{item.body}</div>

      {communicationLink ? (
        <div>
          <Link className="family-section__action" to={communicationLink}>
            連絡内容を開く
          </Link>
        </div>
      ) : null}

      {relatedStudentLabels.length > 0 ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12 }}>
          {relatedStudentLabels.map((studentLabel) => (
            <span key={`${item.itemId}:${studentLabel}`} style={{ padding: "4px 8px", borderRadius: 999, background: "#f6f6f6" }}>
              {studentLabel}
            </span>
          ))}
        </div>
      ) : null}

      {item.targetType && item.targetId ? (
        <div style={{ fontSize: 12, opacity: 0.8 }}>
          対象: {describeStatus(item.targetType)} / {item.targetId}
        </div>
      ) : null}

      {markReadEnabled && !item.readAt && onMarkRead ? (
        <div>
          <button type="button" onClick={() => onMarkRead(item)} disabled={markReadBusy}>
            {markReadBusy ? "既読にしています..." : "既読にする"}
          </button>
        </div>
      ) : null}
    </article>
  );
}
